import React, { memo, useRef, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanCard } from './KanbanCard';
import { ScrollArea } from '../ui/scroll-area';
import { Minimize2, Maximize2, MoreVertical, Lock, Pencil, Trash2, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { KanbanColuna, KanbanTicket } from '../../services/kanbanService';

interface KanbanColumnProps {
  coluna: KanbanColuna;
  tickets: KanbanTicket[];
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  todasColunas: KanbanColuna[];
  onToggleMinimize: (coluna: KanbanColuna) => void;
  onColumnMenu: (e: React.MouseEvent, coluna: KanbanColuna) => void;
  onMoverTicket: (ticketId: string, colunaId: string | null, status?: string) => void;
  onAbrirCampos: (ticket: KanbanTicket) => void;
  onAbrirNota: (ticket: KanbanTicket) => void;
  onAbrirAtendimento: (ticket: KanbanTicket) => void;
  onCardClick: (ticket: KanbanTicket) => void;
  onExcluirColuna?: (coluna: KanbanColuna) => void;
  onConfirmarFechar?: (ticketId: string, colunaId: string) => void;
  canMoveToPendentes?: (fromColunaId: string) => boolean;
  onFixar?: (ticketId: string) => void;
}

export const KanbanColumn = memo(({
  coluna,
  tickets,
  totalCount,
  hasMore,
  isLoadingMore,
  onLoadMore,
  todasColunas,
  onToggleMinimize,
  onColumnMenu,
  onMoverTicket,
  onAbrirCampos,
  onAbrirNota,
  onAbrirAtendimento,
  onCardClick,
  onExcluirColuna,
  onConfirmarFechar,
  canMoveToPendentes,
  onFixar,
}: KanbanColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: coluna.id,
    data: { coluna }
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLoadingMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const isMinimized = coluna.minimizada;
  const isFixa = coluna.tipo === 'fixa';

  if (isMinimized) {
    return (
      <div
        className={`flex flex-col w-[60px] min-w-[60px] bg-gray-50 dark:bg-slate-800/50 
          rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 flex-shrink-0
          ${isOver ? 'ring-2 ring-purple-400 bg-purple-50 dark:bg-purple-900/20' : ''}`}
      >
        <div className="h-[3px] rounded-t-xl" style={{ backgroundColor: coluna.cor || '#6B7280' }} />
        <div className="flex flex-col items-center py-2 gap-1">
          <span
            className="text-xs font-bold text-white w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: coluna.cor || '#6B7280' }}
          >
            {coluna.titulo.charAt(0)}
          </span>
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
            {totalCount}
          </span>
          <button
            onClick={() => onToggleMinimize(coluna)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Expandir coluna"
          >
            <Maximize2 size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col w-[300px] min-w-[300px] max-w-[320px] bg-gray-50 dark:bg-slate-800/50 
        rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 flex-shrink-0
        ${isOver ? 'ring-2 ring-purple-400 bg-purple-50 dark:bg-purple-900/20' : ''}`}
    >
      <div className="h-[3px] rounded-t-xl" style={{ backgroundColor: coluna.cor || '#6B7280' }} />

      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide truncate">
            {coluna.titulo}
          </h3>
          {isFixa && (
            <Lock size={12} className="text-gray-400 dark:text-gray-500 shrink-0" />
          )}
          <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300 shrink-0">
            {totalCount}
          </span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={() => onToggleMinimize(coluna)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title={isMinimized ? 'Expandir coluna' : 'Minimizar coluna'}
          >
            {isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <MoreVertical size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {!isFixa && (
                <>
                  <DropdownMenuItem onClick={() => onColumnMenu({ stopPropagation: () => {} } as any, coluna)}>
                    <Pencil size={14} className="mr-2" />
                    Renomear / Cor
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => onToggleMinimize(coluna)}>
                {isMinimized ? <Maximize2 size={14} className="mr-2" /> : <Minimize2 size={14} className="mr-2" />}
                {isMinimized ? 'Restaurar' : 'Minimizar'}
              </DropdownMenuItem>
              {!isFixa && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20"
                    onClick={() => onExcluirColuna?.(coluna)}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div ref={(node) => { setNodeRef(node); scrollRef.current = node; }} className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto px-2 pb-2 hide-scrollbar" onScroll={handleScroll}>
          <SortableContext items={tickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {tickets.map(ticket => (
                <KanbanCard
                  key={ticket.id}
                  ticket={ticket}
                  colunaId={coluna.id}
                  colunas={todasColunas}
                  onMoverTicket={onMoverTicket}
                  onAbrirCampos={onAbrirCampos}
                  onAbrirNota={onAbrirNota}
                  onAbrirAtendimento={onAbrirAtendimento}
                  onClick={onCardClick}
                  onConfirmarFechar={onConfirmarFechar}
                  canMoveToPendentes={canMoveToPendentes}
                  onFixar={onFixar}
                />
              ))}
            </div>
          </SortableContext>
          {isLoadingMore && (
            <div className="flex items-center justify-center py-3">
              <Loader2 size={16} className="animate-spin text-purple-500" />
            </div>
          )}
          {!isLoadingMore && hasMore && tickets.length > 0 && (
            <button
              onClick={onLoadMore}
              className="w-full py-2 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
            >
              Carregar mais...
            </button>
          )}
          {tickets.length === 0 && !isLoadingMore && (
            <div className="flex items-center justify-center h-20 text-xs text-gray-400 dark:text-gray-500">
              Nenhum ticket
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

KanbanColumn.displayName = 'KanbanColumn';
